import { createRouter } from 'vue-router'
import { applyHead, headFor } from './seo'

// Each page is a separate chunk; the prerender adds <link rel="modulepreload"> for the page's chunks.
const PlayView = () => import('./views/PlayView.vue')
const GenerationView = () => import('./views/GenerationView.vue')
const HomeView = () => import('./views/HomeView.vue')
const HowToView = () => import('./views/HowToView.vue')
const CreateGameView = () => import('./views/CreateGameView.vue')
const LoginView = () => import('./views/LoginView.vue')
const JoinView = () => import('./views/JoinView.vue')
const ProfileView = () => import('./views/ProfileView.vue')
const LobbyView = () => import('./views/LobbyView.vue')
const GamesView = () => import('./views/GamesView.vue')
const AdminProvidersView = () => import('./views/AdminProvidersView.vue')
const AdminGamesView = () => import('./views/AdminGamesView.vue')
const AdminGameView = () => import('./views/AdminGameView.vue')
const AdminGamePacksView = () => import('./views/AdminGamePacksView.vue')
const AdminUsersView = () => import('./views/AdminUsersView.vue')
const AdminPaymentsView = () => import('./views/AdminPaymentsView.vue')
const AdminSubscriptionsView = () => import('./views/AdminSubscriptionsView.vue')
const BuyView = () => import('./views/BuyView.vue')
const LegalView = () => import('./views/LegalView.vue')
const CompanyView = () => import('./views/CompanyView.vue')
const KidsView = () => import('./views/KidsView.vue')
const SoloView = () => import('./views/SoloView.vue')
const NotFoundView = () => import('./views/NotFoundView.vue')

// `prerender` pages are rendered to static HTML at build time and listed in sitemap.xml.
export const routes = [
  {
    path: '/',
    name: 'home',
    component: HomeView,
    meta: {
      prerender: true, priority: '1.0', fullTitle: true, preloadImage: '/assets/original.webp',
      title: 'Промптгесс — игра в картинки и слова с ИИ для компании',
      description: 'Игра для компании: один придумывает сцену, ИИ рисует картинку, остальные описывают её, не называя главного. Вживую или онлайн, одна игра в день бесплатно.'
    }
  },
  {
    path: '/how-to',
    name: 'how-to',
    component: HowToView,
    meta: {
      prerender: true, priority: '0.8', preloadImage: '/assets/original.webp',
      title: 'Как играть: правила и пример раунда',
      description: 'Правила Промптгесс по шагам: комната, запретные слова, описание картинки и подсчёт баллов. Интерактивный учебный раунд и ответы на частые вопросы.'
    }
  },
  {
    path: '/friends',
    name: 'friends',
    component: CompanyView,
    meta: {
      prerender: true, priority: '0.9', image: '/og/friends.jpg', preloadImage: '/assets/company/suitcase-original.webp',
      title: 'Игра для компании друзей с ИИ: вживую и онлайн',
      description: 'Весёлая игра для компании: ИИ рисует картинки, а вы объясняете их другими словами. Играйте за одним столом или онлайн по ссылке из разных городов.'
    }
  },
  {
    path: '/kids',
    name: 'kids',
    component: KidsView,
    meta: {
      prerender: true, priority: '0.9', image: '/og/kids.jpg', preloadImage: '/assets/kids/cupcake-original.webp',
      title: 'Развивающая игра для детей и родителей',
      description: 'Весёлая развивающая игра для детей и родителей: ребёнок придумывает сцену, ИИ её рисует. Тренирует фантазию, образное мышление и речь.'
    }
  },
  {
    path: '/solo',
    name: 'solo',
    component: SoloView,
    meta: {
      prerender: true, priority: '0.9', image: '/og/solo.jpg', preloadImage: '/assets/company/iceberg-original.webp',
      title: 'Игра с ИИ для одного: опиши картинку и обыграй нейросеть',
      description: 'Играйте один на один с ИИ: загадайте сцену, а нейросеть попробует её повторить, не называя главного. Потом ваша очередь. Старт сразу, одна игра в день бесплатно.'
    }
  },
  {
    path: '/create',
    name: 'create',
    component: CreateGameView,
    meta: {
      prerender: true, priority: '0.6',
      title: 'Создать игру',
      description: 'Создайте комнату за минуту: выберите стиль картинок, позовите друзей по коду или ссылке либо сыграйте один на один с ИИ.'
    }
  },
  {
    path: '/buy',
    name: 'buy',
    component: BuyView,
    meta: {
      prerender: true, priority: '0.7', preloadImage: '/assets/subscription-astronaut-cat.webp',
      title: 'Игры и подписка',
      description: 'Пакеты игр и подписка Промптгесс: больше игр в месяц, свой стиль картинок и скидка на пакеты. Друзья присоединяются к оплаченной игре без расхода своей бесплатной.'
    }
  },
  {
    path: '/join',
    name: 'join',
    component: JoinView,
    meta: {
      prerender: true, sitemap: false, robots: 'noindex, follow',
      title: 'Войти в игру по коду',
      description: 'Присоединяйтесь к игре друзей по коду комнаты — без регистрации.'
    }
  },
  {
    path: '/login',
    name: 'login',
    component: LoginView,
    // Old invite links pointed here with ?room=.
    beforeEnter: to => (typeof to.query.room === 'string' ? { name: 'join', query: to.query } : true),
    meta: {
      prerender: true, sitemap: false, robots: 'noindex, follow',
      title: 'Войти в аккаунт',
      description: 'Войдите по почте, чтобы открыть покупки, подписку и историю игр на любом устройстве.'
    }
  },
  {
    path: '/profile',
    name: 'profile',
    component: ProfileView,
    meta: {
      prerender: true, sitemap: false, robots: 'noindex, follow',
      title: 'Профиль',
      description: 'Имя, почта и выход из аккаунта Промптгесс.'
    }
  },
  // Client-only routes: Nginx serves dist/spa.html for them (see docs/deploy-bare-metal.md).
  { path: '/lobby/:id', name: 'lobby', component: LobbyView },
  { path: '/play/:id', name: 'play', component: PlayView },
  { path: '/generate', name: 'generate', component: GenerationView },
  { path: '/games', name: 'games', component: GamesView },
  {
    path: '/privacy',
    name: 'privacy',
    component: LegalView,
    props: { document: 'privacy' },
    meta: {
      prerender: true, priority: '0.2',
      title: 'Политика конфиденциальности',
      description: 'Как Промптгесс обрабатывает персональные данные, cookie и данные браузера.'
    }
  },
  {
    path: '/terms',
    name: 'terms',
    component: LegalView,
    props: { document: 'terms' },
    meta: {
      prerender: true, priority: '0.2',
      title: 'Пользовательское соглашение',
      description: 'Условия использования Промптгесс: игры, покупки, подписка и автопродление.'
    }
  },
  { path: '/admin/providers', name: 'admin-providers', component: AdminProvidersView },
  { path: '/admin/games', name: 'admin-games', component: AdminGamesView },
  { path: '/admin/games/:id', name: 'admin-game', component: AdminGameView },
  { path: '/admin/packs', name: 'admin-packs', component: AdminGamePacksView },
  { path: '/admin/users', name: 'admin-users', component: AdminUsersView },
  { path: '/admin/payments', name: 'admin-payments', component: AdminPaymentsView },
  { path: '/admin/subscriptions', name: 'admin-subscriptions', component: AdminSubscriptionsView },
  // Prerendered separately to dist/404.html, which Nginx returns with status 404.
  { path: '/:pathMatch(.*)*', name: 'not-found', component: NotFoundView, meta: { title: 'Страница не найдена', canonical: false } }
]

export function createAppRouter(history) {
  const router = createRouter({
    history,
    scrollBehavior(to) {
      return to.hash ? { el: to.hash, top: 92, behavior: 'smooth' } : { top: 0 }
    },
    routes
  })

  if (!import.meta.env.SSR) {
    // After a deploy an open tab may request a chunk that no longer exists: load the page afresh.
    router.onError((error, to) => {
      if (/dynamically imported module|Importing a module script failed/i.test(error?.message || '')) window.location.assign(to.fullPath)
    })

    router.afterEach((to, from, failure) => {
      if (failure) return
      applyHead(headFor(to))

      // The HTML counter handles the first load; track subsequent SPA page views here.
      if (from.matched.length) {
        const tmr = window._tmr || (window._tmr = [])
        tmr.push({ id: '3797450', type: 'pageView', url: window.location.href, start: Date.now() })
      }

      window.ym(113052907, 'hit', window.location.href, {
        referer: from.matched.length ? new URL(from.fullPath, window.location.origin).href : document.referrer,
        title: document.title
      })
    })
  }

  return router
}
